import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import localStorageAvailable from '@utils/localStorageAvailable';
//
import { isValidToken, setSession } from '@utils/jwt';
import axiosInstance from 'src/utils/axios';
import { useRouter } from 'node_modules/next/router';
// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
  companyName: null,
  isSuperAdmin: false,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
      companyName: action.payload.companyName,
      isSuperAdmin: action.payload.isSuperAdmin,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
      companyName: action.payload.companyName,
      isSuperAdmin: action.payload.isSuperAdmin,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
      companyName: action.payload.companyName,
      isSuperAdmin: action.payload.isSuperAdmin,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
      companyName: null,
      isSuperAdmin: false,
    };
  }

  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const storageAvailable = localStorageAvailable();
  const router = useRouter();
  const initialize = useCallback(async () => {
    try {
      const accessToken = storageAvailable ? localStorage.getItem('commonAccessToken') : '';
      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        const response = await axiosInstance.get('admin/auth/profile');

        const { user, company_name } = response.data;

        const isSuperAdmin = user?.roles?.map((role) => role.id).includes(1);

        const companyName = company_name;
        dispatch({
          type: 'INITIAL',
          payload: {
            isAuthenticated: true,
            user,
            companyName,
            isSuperAdmin,
          },
        });
      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            isAuthenticated: false,
            user: null,
            companyName: null,
            isSuperAdmin: false,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: 'INITIAL',
        payload: {
          isAuthenticated: false,
          user: null,
          companyName: null,
          isSuperAdmin: false,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageAvailable, router, initialize, state.isAuthenticated]);

  useEffect(() => {
    initialize();
  }, [initialize, router, storageAvailable, state.isAuthenticated]);

  // LOGIN
  const login = useCallback(async (accessToken, user) => {
    // const response = await axiosInstance.post('admin/auth/signin', {
    //   email,
    //   password,
    // });
    // const { accessToken, user } = response.data;
    initialize();
    setSession(accessToken);

    const isSuperAdmin = user?.roles?.map((role) => role.id).includes(1);

    dispatch({
      type: 'LOGIN',
      payload: {
        isAuthenticated: true,
        user,
        isSuperAdmin,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // REGISTER
  const register = useCallback(async (email, password, firstName, lastName) => {
    const response = await axiosInstance.post('admin/auth/signup', {
      email,
      password,
      firstName,
      lastName,
    });
    const { accessToken, user } = response.data;

    localStorage.setItem('accessToken', accessToken);

    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    });
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    await axiosInstance.delete('/admin/auth/signout').then((response) => {
      if (response.status === 200) {
        setSession(null);
        dispatch({ type: 'LOGOUT' });
      }
    });
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      isSuperAdmin: state.isSuperAdmin,
      user: state.user,
      method: 'jwt',
      login,
      register,
      logout,
    }),
    [state.isAuthenticated, state.isInitialized, state.isSuperAdmin, state.user, login, logout, register]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
